import { useEffect } from "react";
import { useRouter } from "next/router";

/**
 * Warms a small set of route bundles so a later `router.push` is instant.
 *
 * Why this exists: mobile navigation in this app is plain `<button>` +
 * `router.push` (the bottom nav tabs, the menu rows, the header back arrow), and
 * `router.push` — unlike `next/link` — never prefetches. Desktop never noticed,
 * because `DesktopSidebar`'s `<Link>`s sit in the viewport and Next prefetches
 * them for free. On mobile the sidebar is hidden, so nothing was ever warmed and
 * every tap paid the destination's chunk download on the spot: measured at 663 ms
 * of a 758 ms back-tap, entirely between routeChangeStart and
 * beforeHistoryChange, with no main-thread work at all.
 *
 * Use this for a handful of light, high-probability destinations. For a heavy
 * route (anything pulling the PDF engine) prefetch on intent instead — see
 * `components/IntentLink`.
 */
/**
 * Routes whose bundle carries @react-pdf/renderer (~1.2 MB parsed). Never warm
 * these eagerly — check the build manifest if you think the list has changed:
 *   node -e "const m=require('./.next/build-manifest.json');..."
 */
export const HEAVY_ROUTES = ["/reports", "/valuation/report/[id]"];

export const isHeavyRoute = url =>
  !!url && HEAVY_ROUTES.some(r => url === r || url.startsWith(r.replace("/[id]", "/")));

export default function usePrefetchRoutes(routes, enabled = true) {
  const router = useRouter();
  const key = Array.isArray(routes) ? routes.join("|") : "";

  useEffect(() => {
    if (!enabled || !key) return;
    let cancelled = false;

    // After paint, and off the critical path: warming a route must never
    // compete with rendering the page the user is actually looking at.
    const warm = () => {
      if (cancelled) return;
      for (const url of key.split("|")) {
        // A heavy route is never warmed eagerly, whatever the caller passed.
        if (url && url !== router.pathname && !isHeavyRoute(url)) {
          router.prefetch(url).catch(() => {});
        }
      }
    };
    const idle =
      typeof window !== "undefined" && window.requestIdleCallback
        ? window.requestIdleCallback(warm, { timeout: 2000 })
        : setTimeout(warm, 300);

    return () => {
      cancelled = true;
      if (typeof window !== "undefined" && window.cancelIdleCallback && window.requestIdleCallback) {
        window.cancelIdleCallback(idle);
      } else {
        clearTimeout(idle);
      }
    };
  }, [key, enabled, router]);
}
